/**
 * Monkey Worlds — pure gameplay predicates, shared by the game runtime and Vitest.
 *
 * The runtime is a self-contained HTML string (monkeyWorldsGameHtml.ts) that Vitest cannot
 * import, so the §9 T-1..T-6 rules (progress packing, unlocks, stars, stomp/hurt, world verbs,
 * physics edges) are enforced by these pure functions: the runtime embeds their SOURCE via
 * getMonkeyWorldsScoringSource() (Function.prototype.toString), and tests import them directly —
 * one implementation, zero drift. The server re-implements only pack/unpack; T-17 keeps them in
 * lockstep.
 *
 * Constraints for the embedding to stay valid: plain function declarations only, ES5-style bodies
 * (var, no arrow fns), and EVERY embedded function must be a LEAF — no reference to a module-scope
 * const and no call to another module function. Minification renames those bindings while the
 * runtime defines only the canonical names, so such a reference throws `<mangled> is not defined`
 * on every frame of a release build (it cost us a chain-clearing progress bug and a dead shootout
 * on staging). Tuning values are passed in as arguments instead; `leafInvariant` in the test suite
 * enforces this by running the embedded source with nothing but Math/JSON in scope.
 */

/** Tuned feel constants. Injected into the runtime as JSON; mirrored nowhere else. */
export const MONKEY_WORLDS_PHYSICS = {
  GRAVITY: 0.005, // tiles/tick² → ~5.3-tile jump apex with JUMP_VEL (apex = v²/2g)
  MAX_FALL: 0.3, // tiles/tick — fall cap
  RUN_ACCEL: 0.01,
  RUN_MAX: 0.15, // ~9 tiles/s at 60 Hz
  AIR_CONTROL: 0.7,
  JUMP_VEL: -0.23, // ~5.3-tile apex; +double jump clears ~9
  JUMP_CUT: 0.45, // multiply vy on early release — ONLY for a jump the player initiated
  COYOTE_TICKS: 6,
  BUFFER_TICKS: 8,
  SWING_ARC_TICKS: 12,
  CHARGE_MAX_TICKS: 42, // shot power meter sweeps 0→1 over this, then back down
  ENEMY_SPEED: 0.045,
  ENEMY_CHARGE_SPEED: 0.1,
  ENEMY_CHARGE_TICKS: 46,
  BALL_GRAVITY: 0.0028, // the shot ball floats a bit longer than the player
  BALL_BOUNCE: 0.55, // ground rebound damping — a missed shot rolls on toward the goal
  BALL_LIFE_TICKS: 320,
  STUN_TICKS: 90,
  INVULN_TICKS: 80,
  HEARTS: 3,
  // v2 movement kit
  AIR_JUMPS: 1, // double jump
  SLIDE_TICKS: 36,
  SLIDE_H: 0.5, // hitbox height while sliding (fits 1-tile gaps + under hurdles)
  SLIDE_BOOST: 1.35, // × RUN_MAX at slide start, decaying
  ROPE_LEN: 4, // tiles
  ROPE_DAMP: 0.995,
  ROPE_PUMP: 0.0012, // angular accel per tick of held direction
  ROPE_REGRAB_TICKS: 25,
  JET_FUEL_TICKS: 240,
  JET_THRUST: 0.012,
  JET_MAX_RISE: 0.22,
  SPRING_VY: -0.33, // ~10.9-tile apex — twice a regular jump; never jump-cut (see cutArmed)
} as const;

/**
 * Pack four per-world furthest-slot values (0-6) into one uint32: byte `w` = world `w`,
 * world 0 in the least-significant byte. This is what the server stores in `game_streaks.max`.
 */
export function packWorldProgress(bytes: number[]): number {
  var packed = 0;
  for (var i = 0; i < 4; i++) {
    var b = bytes[i];
    var v = typeof b === 'number' && b > 0 ? Math.min(255, Math.floor(b)) : 0;
    packed += v * Math.pow(256, i);
  }
  return packed;
}

/** Inverse of packWorldProgress. Invalid/negative/oversized inputs unpack as all-zero. */
export function unpackWorldProgress(packed: number): number[] {
  var out = [0, 0, 0, 0];
  if (typeof packed !== 'number' || !isFinite(packed) || packed < 0 || packed > 4294967295) {
    return out;
  }
  var rest = Math.floor(packed);
  for (var i = 0; i < 4; i++) {
    out[i] = rest % 256;
    rest = Math.floor(rest / 256);
  }
  return out;
}

/**
 * Within a world, slot 1 is always open and slot n+1 opens once slot n is cleared.
 * `furthestSlot` is that world's unpacked byte (0 = nothing cleared).
 */
export function isLevelUnlockedInWorld(slot: number, furthestSlot: number): boolean {
  if (typeof slot !== 'number' || slot < 1 || slot > 6 || slot !== Math.floor(slot)) return false;
  var furthest = typeof furthestSlot === 'number' && furthestSlot > 0 ? furthestSlot : 0;
  return slot <= furthest + 1;
}

/**
 * Star rating for a clear: par-time, all-coins and no-damage are the three feats.
 * 3 stars = under par AND (all coins OR no damage); 2 = any two feats; else 1.
 */
export function levelStars(
  clearTimeTicks: number,
  timeParTicks: number,
  coinsCollected: number,
  coinTotal: number,
  tookDamage: boolean,
  shotsTaken: number,
  starShots: number
): 1 | 2 | 3 {
  var underPar = clearTimeTicks <= timeParTicks;
  var allCoins = coinTotal > 0 ? coinsCollected >= coinTotal : true;
  var noDamage = !tookDamage;
  // attempts are not capped, so accuracy is rewarded here rather than punished with a fail
  var sharp = typeof shotsTaken !== 'number' || shotsTaken <= starShots;
  var feats = (underPar ? 1 : 0) + (allCoins ? 1 : 0) + (noDamage ? 1 : 0) + (sharp ? 1 : 0);
  if (sharp && underPar && (allCoins || noDamage)) return 3;
  if (feats >= 2) return 2;
  return 1;
}

/**
 * Player-vs-enemy contact: falling onto the enemy's head is a stomp; a stunned enemy or an
 * invulnerable player never hurts; anything else costs a heart. `playerBottom`/`enemyTop` are
 * world-Y (down positive), `playerVy` positive = falling.
 */
export function playerEnemyOutcome(
  playerVy: number,
  playerBottom: number,
  enemyTop: number,
  enemyStunned: boolean,
  playerInvulnerable: boolean
): 'stomp' | 'hurt' | 'none' {
  var fromAbove = playerVy > 0 && playerBottom <= enemyTop + 0.35;
  if (fromAbove) return 'stomp';
  if (enemyStunned || playerInvulnerable) return 'none';
  return 'hurt';
}

/** A shot ball breaks breakable blocks only (never ground/charge-walls). */
export function kickBreaksTile(tileChar: string): boolean {
  return tileChar === 'B';
}

/**
 * Diamond verb: the bat reflects a projectile that is travelling toward the player
 * (moving opposite to facing). Returns the reflected vx (re-owned shot) or 0 for a whiff.
 */
export function swingReflectsProjectile(projVx: number, facing: number): number {
  var incoming = (facing >= 0 && projVx < 0) || (facing < 0 && projVx > 0);
  if (!incoming) return 0;
  return -projVx * 1.25;
}

/** A powered shot smashes through charge-walls (never ground). */
export function ballBreaksWall(tileChar: string): boolean {
  return tileChar === 'W';
}

/**
 * The finale — a composed "shootout" (penalty / free throw / at-bat / field goal) instead of a
 * goal structure jammed into the scrolling terrain. Two timed decisions, both readable:
 *   1. an aim marker sweeps across the goal mouth — tap to place the ball,
 *   2. a power meter sweeps — tap to strike.
 * Everything below is in NORMALISED mouth coordinates (-1 = left post, +1 = right post), so the
 * rules are pure and the scene can be laid out in screen space at any viewport size.
 */
export const MONKEY_WORLDS_SHOOTOUT = {
  /** Aim sweep period (ticks) at slot 1; each later slot is quicker. */
  AIM_PERIOD: 96,
  /** Power meter sweep period (ticks). */
  POWER_PERIOD: 52,
  /** Under this the shot falls short; over it, it sails. ~40% of the meter is on target. */
  POWER_MIN: 0.42,
  POWER_MAX: 0.84,
  /** Half-width of the keeper's reach, in mouth units. */
  KEEPER_HALF: 0.3,
  /** Keeper patrol half-range — it cannot quite cover the posts. */
  KEEPER_RANGE: 0.72,
  /** Attempts are unlimited (no soft-lock); the count feeds the star rating instead. */
  STAR_SHOTS: 2,
} as const;

/** Where the keeper is (mouth units) at a given tick — deterministic, so it can be learned. */
export function shootoutKeeperAt(
  tick: number,
  slot: number,
  cfg: { AIM_PERIOD: number; KEEPER_RANGE: number }
): number {
  var S = cfg;
  var period = Math.max(40, S.AIM_PERIOD - (Math.min(6, Math.max(1, slot)) - 1) * 9);
  return Math.sin((tick * 2 * Math.PI) / period) * S.KEEPER_RANGE;
}

/**
 * The result of an attempt. Order matters: a shot with the wrong power never reaches the mouth,
 * so it is short/over regardless of placement; otherwise the keeper's reach decides it.
 */
export function shootoutOutcome(
  aimX: number,
  keeperX: number,
  power: number,
  cfg: { POWER_MIN: number; POWER_MAX: number; KEEPER_HALF: number }
): string {
  var S = cfg;
  if (power < S.POWER_MIN) return 'short';
  if (power > S.POWER_MAX) return 'over';
  if (Math.abs(aimX) > 1) return 'wide';
  if (Math.abs(aimX - keeperX) < S.KEEPER_HALF) return 'saved';
  return 'goal';
}

/**
 * Shootout composition, derived entirely from the viewport. Fixed pixel offsets have broken this
 * scene twice on short landscape screens — the shooter ended up standing inside the goal and the
 * prompt fell off the bottom — so the geometry is a pure function that T-38 exercises across the
 * whole range of phone viewports instead of being eyeballed at one size.
 *
 * Returns screen-space pixels. `goalTop`..`groundY` is the goal; the shooter stands below it; the
 * prompt and power meter sit above the bottom edge.
 */
export function shootoutLayout(
  W: number,
  H: number,
  insetTop: number
): {
  goalW: number;
  goalH: number;
  goalTop: number;
  groundY: number;
  shooterY: number;
  promptY: number;
  meterY: number;
} {
  var topPad = insetTop + 46; // title + attempt line
  var botPad = 78; // shooter, prompt, meter
  var band = Math.max(80, H - topPad - botPad);
  var goalH = Math.min(band * 0.66, W * 0.78 * 0.56);
  var goalW = Math.min(W * 0.78, goalH / 0.56);
  var goalTop = topPad;
  var groundY = goalTop + goalH;
  var shooterY = Math.min(H - 22, groundY + Math.max(26, (H - groundY) * 0.5));
  return {
    goalW: goalW,
    goalH: goalH,
    goalTop: goalTop,
    groundY: groundY,
    shooterY: shooterY,
    promptY: H - 30,
    meterY: H - 20,
  };
}

/**
 * Shot power from how long the button is held: a meter that sweeps 0→1→0 over 2×maxTicks and
 * repeats. You pick your power by *when you release*, which is what turns the shot from a button
 * press into an aimed attempt.
 */
export function shotPower(holdTicks: number, maxTicks: number): number {
  if (!(maxTicks > 0)) return 1;
  var phase = holdTicks % (maxTicks * 2);
  var up = phase <= maxTicks;
  var v = up ? phase / maxTicks : 2 - phase / maxTicks;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Launch multiplier for a given power — never zero, so a mistimed release still travels. */
export function shotSpeedScale(power: number): number {
  var p = power < 0 ? 0 : power > 1 ? 1 : power;
  return 0.55 + 0.9 * p;
}



/**
 * A ground enemy breaks into a charge when the player is close, roughly level with it and in
 * front of it — so later levels punish walking straight at things.
 */
export function enemyShouldCharge(dx: number, dy: number, facing: number, slot: number): boolean {
  if (slot < 3) return false;
  if (Math.abs(dy) > 1.2) return false;
  var ahead = facing >= 0 ? dx > 0 : dx < 0;
  return ahead && Math.abs(dx) < 7;
}


/** Axis-aligned box overlap (x,y = top-left; sizes > 0). */
export function aabbOverlap(
  ax: number,
  ay: number,
  aw: number,
  ah: number,
  bx: number,
  by: number,
  bw: number,
  bh: number
): boolean {
  return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

/**
 * One-axis collision resolution: given a moving box (min edge + size + velocity) overlapping a
 * solid tile span, return the corrected min edge — pushed back against the direction of travel.
 * Zero velocity resolves toward the smaller penetration.
 */
export function resolveAxis(
  boxMin: number,
  boxSize: number,
  vel: number,
  tileMin: number,
  tileSize: number
): number {
  var overlaps = boxMin < tileMin + tileSize && boxMin + boxSize > tileMin;
  if (!overlaps) return boxMin;
  if (vel > 0) return tileMin - boxSize;
  if (vel < 0) return tileMin + tileSize;
  var leftPen = boxMin + boxSize - tileMin;
  var rightPen = tileMin + tileSize - boxMin;
  return leftPen <= rightPen ? tileMin - boxSize : tileMin + tileSize;
}

/**
 * Forgiving jump: allowed while grounded-or-recently-grounded (coyote) whenever the press is
 * recent enough (buffer). Both windows are inclusive tick counts since the event.
 */
export function coyoteJumpAllowed(
  ticksSinceGrounded: number,
  ticksSincePressed: number,
  coyoteTicks: number,
  bufferTicks: number
): boolean {
  return ticksSinceGrounded <= coyoteTicks && ticksSincePressed <= bufferTicks;
}

/**
 * Rope release (the Spider-swing): angle is measured from vertical (0 = hanging straight down,
 * positive = swung toward +x, y grows downward), angVel in radians/tick. The release velocity is
 * the pendulum's tangential velocity, so momentum carries: swinging right through the bottom
 * launches right, releasing high on the right side launches up-and-right.
 */
export function ropeReleaseVelocity(
  angle: number,
  angVel: number,
  len: number
): { vx: number; vy: number } {
  var speed = angVel * len;
  return { vx: speed * Math.cos(angle), vy: -speed * Math.sin(angle) };
}

/** Deterministic lift path: sine around the base, full cycle every periodTicks. */
export function platformPosAt(base: number, range: number, periodTicks: number, tick: number): number {
  if (!(periodTicks > 0)) return base;
  return base + Math.sin((tick * 2 * Math.PI) / periodTicks) * (range / 2);
}

/** Double jump: only once airborne past the coyote window, while air jumps remain. */
export function airJumpAllowed(
  airJumpsLeft: number,
  grounded: boolean,
  ticksSinceGrounded: number,
  coyoteTicks: number
): boolean {
  return !grounded && ticksSinceGrounded > coyoteTicks && airJumpsLeft > 0;
}

/** Hurdles block runners, never sliders. */
export function hurdleBlocks(sliding: boolean): boolean {
  return !sliding;
}

/** Standing back up from a slide needs headroom — solid overhead keeps you low. */
export function slideCanStand(tileAboveChar: string): boolean {
  return tileAboveChar !== '#' && tileAboveChar !== 'B' && tileAboveChar !== 'W';
}

/** Pit respawn: the furthest checkpoint at-or-before the player's x, else the spawn. */
export function respawnPointFor(
  checkpoints: Array<{ x: number; y: number }>,
  spawn: { x: number; y: number },
  playerX: number
): { x: number; y: number } {
  var best = spawn;
  var bestX = -Infinity;
  for (var i = 0; i < checkpoints.length; i++) {
    var cp = checkpoints[i];
    if (cp.x <= playerX && cp.x > bestX) {
      best = cp;
      bestX = cp.x;
    }
  }
  return best;
}

/**
 * Canonical runtime identifier → predicate. The runtime's <script> calls these EXACT names, so each
 * must be bound to its canonical name in the embedded source. `Function.prototype.toString()` alone
 * is NOT enough: production minification (Metro/terser) mangles `Function.name`, so a bare
 * `fn.toString()` embeds `function o(...)` and the game loop then throws a ReferenceError every
 * frame — a frozen game. (Dev/unminified builds keep the names, which is why this only bites in
 * production; see blockyStrikerScoring.ts for the original incident.)
 */
const EMBEDDED_FNS = [
  ['packWorldProgress', packWorldProgress],
  ['unpackWorldProgress', unpackWorldProgress],
  ['isLevelUnlockedInWorld', isLevelUnlockedInWorld],
  ['levelStars', levelStars],
  ['playerEnemyOutcome', playerEnemyOutcome],
  ['kickBreaksTile', kickBreaksTile],
  ['shotPower', shotPower],
  ['shootoutLayout', shootoutLayout],
  ['shootoutOutcome', shootoutOutcome],
  ['shootoutKeeperAt', shootoutKeeperAt],
  ['shotSpeedScale', shotSpeedScale],
  ['enemyShouldCharge', enemyShouldCharge],
  ['swingReflectsProjectile', swingReflectsProjectile],
  ['ballBreaksWall', ballBreaksWall],
  ['aabbOverlap', aabbOverlap],
  ['resolveAxis', resolveAxis],
  ['coyoteJumpAllowed', coyoteJumpAllowed],
  ['ropeReleaseVelocity', ropeReleaseVelocity],
  ['platformPosAt', platformPosAt],
  ['airJumpAllowed', airJumpAllowed],
  ['hurdleBlocks', hurdleBlocks],
  ['slideCanStand', slideCanStand],
  ['respawnPointFor', respawnPointFor],
] as const;

export const MONKEY_WORLDS_EMBEDDED_FN_NAMES = EMBEDDED_FNS.map(([name]) => name);

/**
 * The predicates as embeddable JS source for the runtime's <script> block. Each is assigned to its
 * canonical name (`var NAME = <source>;`) so it resolves regardless of how minification renamed
 * the function itself. Bodies stay transform-proof per the header (ES5, no module captures).
 */
export function getMonkeyWorldsScoringSource(): string {
  return EMBEDDED_FNS.map(([name, fn]) => `var ${name} = ${fn.toString()};`).join('\n');
}
